import React, { useEffect, useMemo, useRef, useState } from "react";
import CombatModal from "./../CombatModal";
import EditCombatModal from "./../EditCombatModal";
import { getDistributionCombats } from "../../services/combatsServices";
import { calculateClassementPoule } from "../../utils/classementPouleUtils";
import { updateCompetiteurResults } from "../../services/competiteursServices";
import { finalizeDistribution } from "../../services/distributionsServices";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import CombatDetailsModal from "./components/CombatDetailsModal";

/* ------------------------------ Helpers ------------------------------ */

// id de combat "unique" pour une paire (ordre indifférent)
const pairKey = (id1, id2) => {
  const a = Number(id1);
  const b = Number(id2);
  return a < b ? `${a}_${b}` : `${b}_${a}`;
};

const readPos = (c, fallbackIndex) => {
  const candidates = [
    c._pos,
    c.position,
    c.position_aleatoire,
    c.poule_position,
    c.distribution_position,
    c?.DistributionCompetiteur?.position,
  ];
  for (const v of candidates) {
    const n = Number(v);
    if (!Number.isNaN(n) && n > 0) return n;
  }
  return fallbackIndex + 1;
};

const capitalizeFr = (str = "") => {
  const s = String(str).toLocaleLowerCase("fr-FR");
  if (!s) return "";
  return s.charAt(0).toLocaleUpperCase("fr-FR") + s.slice(1);
};

const resultColor = (r) => {
  switch (r) {
    case "V":
      return "bg-emerald-200 text-emerald-900";
    case "D":
      return "bg-rose-200 text-rose-900";
    case "N":
      return "bg-amber-200 text-amber-900";
    case "H":
      return "bg-red-600 text-white";
    case "F":
      return "bg-slate-500 text-white";
    default:
      return "bg-gray-100";
  }
};

/* ------------------------------ Component ------------------------------ */

const RepartitionPouleDeux = ({
  competiteurs,
  distributionId,
  refreshDistribution,
  competitionId,
  tapisId,
  distributionCompetiteurs, // 🆕 passer l’ordre d’IDs de la distribution
  genre, // 👈 new
  categorie_age, // 👈 new
  categorie_poids, // 👈 new
  discipline, // 👈 new
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCombat, setSelectedCombat] = useState(null);
  const [positions, setPositions] = useState({
    position1: null,
    position2: null,
  });

  const [combats, setCombats] = useState([]);
  const [classement, setClassement] = useState([]);
  const [classementVisible, setClassementVisible] = useState(false);
  const [isFinalized, setIsFinalized] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const printRef = useRef();

  // Deux compétiteurs (ordre de distribution)
  const orderedCompetiteurs = useMemo(() => {
    const withPos = (competiteurs || []).map((c, i) => ({
      ...c,
      __pos: readPos(c, i),
    }));
    return withPos.sort((a, b) => a.__pos - b.__pos);
  }, [competiteurs]);

  const c1 = orderedCompetiteurs[0];
  const c2 = orderedCompetiteurs[1];

  // Chargement combats
  useEffect(() => {
    fetchCombats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [distributionId]);

  const fetchCombats = async () => {
    try {
      const fetched = await getDistributionCombats(distributionId);
      setCombats(fetched || []);
      updateClassement(fetched || []);
    } catch (error) {
      console.error("Erreur lors de la récupération des combats :", error);
    }
  };

  const refreshPouleTable = () => fetchCombats();

  const updateClassement = (fetchedCombats) => {
    const c = calculateClassementPoule(orderedCompetiteurs, fetchedCombats);
    setClassement(c);
  };

  /* ---------- Mapping “Aller/Retour/Décisif” SANS combat_index ----------
     On filtre les combats de cette poule qui concernent exactement le duo (c1,c2),
     puis on les trie par id croissant (ordre de création) et on leur assigne
     des index virtuels : 1 = Aller, 2 = Retour, 3 = Décisif.
  ----------------------------------------------------------------------- */
  const pairCombatsSorted = useMemo(() => {
    if (!c1?.id || !c2?.id) return [];
    const id1 = Number(c1.id);
    const id2 = Number(c2.id);
    return (combats || [])
      .filter((m) => {
        const a = Number(m.competiteur_id1);
        const b = Number(m.competiteur_id2);
        return (a === id1 && b === id2) || (a === id2 && b === id1);
      })
      .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0));
  }, [combats, c1?.id, c2?.id]);

  const getCombatByIndex = (idx) => pairCombatsSorted[idx - 1] || null;

  // Besoin d’un 3e combat ? (égalité parfaite victoires + points après 1 & 2)
  const needThirdCombat = () => {
    const A = getCombatByIndex(1);
    const B = getCombatByIndex(2);
    if (!A || !B) return false;

    const v1 =
      (A.resultat_competiteur1 === "V" ? 1 : 0) +
      (B.resultat_competiteur1 === "V" ? 1 : 0);
    const v2 =
      (A.resultat_competiteur2 === "V" ? 1 : 0) +
      (B.resultat_competiteur2 === "V" ? 1 : 0);
    if (v1 !== v2) return false;

    const p1 =
      (Number(A.points_competiteur1) || 0) +
      (Number(B.points_competiteur1) || 0);
    const p2 =
      (Number(A.points_competiteur2) || 0) +
      (Number(B.points_competiteur2) || 0);
    return p1 === p2;
  };

  // Ouverture modales (création/édition) – toujours même duo
  const openModal = (idx) => {
    const existing = getCombatByIndex(idx);
    if (existing) {
      setSelectedCombat({
        ...existing,
        competiteur1: c1,
        competiteur2: c2,
      });
      setPositions({ position1: 1, position2: 2 });
      setIsEditModalOpen(true);
    } else {
      const initial = {
        competiteur1: c1,
        competiteur2: c2,
      };
      setSelectedCombat(initial);
      setPositions({ position1: 1, position2: 2 });
      setIsModalOpen(true);
    }
  };

  const handleModalSubmit = async () => {
    await refreshPouleTable();
  };

  // Finalisation
  const confirmAndFinalize = async () => {
    const ok = window.confirm(
      "⚠️ Finaliser la distribution ?\nCette action est irréversible et figera le classement.",
    );
    if (!ok) return;
    await handleFinalizeDistribution();
  };

  const handleFinalizeDistribution = async () => {
    await updateAllCompetiteursResults();
    const response = await finalizeDistribution(distributionId);
    if (response) {
      alert("Distribution finalisée avec succès !");
      setIsFinalized(true);
      refreshDistribution?.();
    }
  };

  const updateAllCompetiteursResults = async () => {
    for (const c of classement) {
      if (!c?.id) continue;

      const payload = {
        classement_position: c.position || null,
        total_victoires: c.victoires || 0,
        total_points: c.points || 0,
        is_disqualified: !!c.disqualifie, // vraie exclusion compétition seulement
      };

      try {
        await updateCompetiteurResults(c.id, payload);
      } catch (e) {
        console.error("Update competiteur error", c.id, e);
      }
    }
  };

  // export PDF
  const handleExportToPDF = async () => {
    const input = printRef.current;
    if (!input) return;

    try {
      // ---- Création du wrapper ----
      const wrapper = document.createElement("div");
      wrapper.style.background = "#ffffff";
      wrapper.style.color = "#000000";
      wrapper.style.padding = "36pt";
      wrapper.style.fontFamily = "Arial, sans-serif";
      wrapper.style.fontSize = "12px";
      wrapper.style.width = "1122pt";

      // ---- En-tête ----
      const header = document.createElement("div");
      header.style.display = "flex";
      header.style.justifyContent = "space-between";
      header.style.alignItems = "flex-start";
      header.style.marginBottom = "14pt";
      header.innerHTML = `
      <div>
        <div style="font-size:16pt;font-weight:800;margin-bottom:2pt;">Poule de 2</div>
        <div style="font-size:10pt;color:#333;">
          Distribution n° <strong>${distributionId}</strong>
        </div>
      </div>
      <div style="text-align:right;font-size:10pt;color:#333;">
        <div><strong>Sexe :</strong> ${(genre || "—").toUpperCase()}</div>
        <div><strong>Cat. âge :</strong> ${categorie_age || "—"}</div>
        <div><strong>Cat. poids :</strong> ${categorie_poids || "—"}</div>
      </div>
    `;
      wrapper.appendChild(header);

      // ---- Tableau principal cloné ----
      const clone = input.cloneNode(true);

      // 🆕 SI AUCUN COMBAT SAISI → vider V / Pts / Classement dans le CLONE
      if (!combats || combats.length === 0) {
        const bodyRows = clone.querySelectorAll("table.print-table tbody tr");

        bodyRows.forEach((tr) => {
          const cells = tr.querySelectorAll("td");
          // on s'assure qu'il y a bien assez de colonnes
          if (cells.length >= 3) {
            // colonnes V, Pts, Classement = les 3 dernières
            cells[cells.length - 3].textContent = "";
            cells[cells.length - 2].textContent = "";
            cells[cells.length - 1].textContent = "";
          }
        });
      }

      wrapper.appendChild(clone);

      // ---- Tableau détails_combat ----
      const rows = Array.isArray(combats) ? [...combats] : [];
      rows.sort((a, b) => (Number(a?.id) || 0) - (Number(b?.id) || 0)); // dernier en bas

      const detailsTitle = document.createElement("div");
      detailsTitle.textContent = "Détails des combats";
      detailsTitle.style.fontWeight = "800";
      detailsTitle.style.fontSize = "12pt";
      detailsTitle.style.margin = "24pt 0 6pt 0";
      wrapper.appendChild(detailsTitle);

      const detailsTable = document.createElement("table");
      detailsTable.style.width = "100%";
      detailsTable.style.borderCollapse = "collapse";
      detailsTable.innerHTML = `
      <thead>
        <tr style="background:#f3f4f6;">
          <th style="text-align:left;border:1px solid #e5e7eb;padding:6pt 8pt;width:60pt;">ID</th>
          <th style="text-align:left;border:1px solid #e5e7eb;padding:6pt 8pt;">Détails du combat</th>
        </tr>
      </thead>
      <tbody>
        ${
          rows.length
            ? rows
                .map((m) => {
                  const id = m?.id ?? "—";
                  const det =
                    (m?.details_combat && String(m.details_combat)) || "—";
                  const detHL = det.replace(
                    /^Gagnant\s*:\s*([^•|]+)/,
                    (_m, p1) =>
                      `<span style="background:#dcfce7;border:1px solid #86efac;padding:0 4pt;border-radius:4pt;"><strong>Gagnant : ${p1.trim()}</strong></span>`,
                  );
                  return `
                    <tr>
                      <td style="border:1px solid #e5e7eb;padding:6pt 8pt;font-weight:600;">${id}</td>
                      <td style="border:1px solid #e5e7eb;padding:6pt 8pt;">
                        <div style="white-space:pre-wrap;line-height:1.35">${detHL}</div>
                      </td>
                    </tr>
                  `;
                })
                .join("")
            : `<tr><td colspan="2" style="border:1px solid #e5e7eb;padding:10pt 8pt;color:#6b7280;text-align:center;">Aucun combat saisi pour l’instant.</td></tr>`
        }
      </tbody>
    `;
      wrapper.appendChild(detailsTable);

      // ---- Placement temporaire ----
      const staging = document.createElement("div");
      staging.style.position = "fixed";
      staging.style.left = "-200vw";
      staging.appendChild(wrapper);
      document.body.appendChild(staging);

      // ---- Capture avec nettoyage oklch ----
      const canvas = await html2canvas(wrapper, {
        backgroundColor: "#ffffff",
        useCORS: true,
        scale: 1,
        logging: false,
        removeContainer: true,
        onclone: (doc) => {
          doc.querySelectorAll("*").forEach((el) => {
            const cs = window.getComputedStyle(el);
            if (cs.backgroundColor.includes("oklch"))
              el.style.backgroundColor = "#ffffff";
            if (cs.color.includes("oklch")) el.style.color = "#000000";
          });
        },
      });

      document.body.removeChild(staging);

      // ---- Export PDF ----
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF({
        orientation: "landscape",
        unit: "pt",
        format: "a4",
      });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let y = 0;
      if (imgHeight <= pageHeight) {
        pdf.addImage(imgData, "PNG", 0, 0, imgWidth, imgHeight);
      } else {
        while (y < imgHeight) {
          pdf.addImage(imgData, "PNG", 0, -y, imgWidth, imgHeight);
          y += pageHeight;
          if (y < imgHeight) pdf.addPage();
        }
      }

      pdf.save(`poule-2-distribution-${distributionId}.pdf`);
    } catch (err) {
      console.error("Erreur export PDF :", err);
    }
  };

  // --- Stats progression (même logique que les autres poules) ---
  // on ne compte que les combats entre les 2 compétiteurs de la poule
  const uniqueCombatsDone = useMemo(() => {
    if (!c1?.id || !c2?.id) return 0;
    const setPairs = new Set();
    for (const m of combats) {
      const k = pairKey(m.competiteur_id1, m.competiteur_id2);
      // ne compter que la paire c1/c2
      const kWanted = pairKey(c1.id, c2.id);
      if (k === kWanted) setPairs.add(k);
    }
    // setPairs.size vaudra 0 ou 1 (une seule paire), on veut le nb de combats de cette paire
    // => on renvoie le nombre réel d'entrées pour la paire c1/c2
    const countForPair = combats.filter(
      (m) =>
        pairKey(m.competiteur_id1, m.competiteur_id2) ===
        pairKey(c1?.id, c2?.id),
    ).length;
    return countForPair;
  }, [combats, c1?.id, c2?.id]);

  // MAX_COMBATS = 2 normalement ; 3 si le décisif est requis (égalité parfaite)
  // ou s'il existe déjà (pour conserver l'affichage cohérent après saisie)
  const MAX_COMBATS = useMemo(() => {
    return needThirdCombat() || Boolean(getCombatByIndex(3)) ? 3 : 2;
  }, [needThirdCombat, getCombatByIndex]);

  const remaining = Math.max(0, MAX_COMBATS - uniqueCombatsDone);
  const percent = Math.round((uniqueCombatsDone / MAX_COMBATS) * 100);

  if (isFinalized) return null;

  // Tooltips (details_combat si dispo)
  const tooltipFor = (idx, labelFallback) => {
    const c = getCombatByIndex(idx);
    if (!c) return `Cliquez pour saisir le ${labelFallback}`;
    return (
      (c.details_combat && String(c.details_combat).trim()) ||
      `Résultat: ${c.resultat_competiteur1 ?? "-"} / ${
        c.resultat_competiteur2 ?? "-"
      } · Points: ${c.points_competiteur1 ?? 0} - ${c.points_competiteur2 ?? 0}`
    );
  };

  /* ------------------------------ Render ------------------------------ */

  return (
    <div className="max-w-[1280px] mx-auto">
      {/* Header + actions */}
      <div className="mb-3 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
        <div className="flex items-start gap-3">
          <div className="shrink-0 h-12 w-12 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center font-extrabold">
            #{distributionId}
          </div>
          <div>
            <h2 className="text-xl font-bold leading-tight">Poule de 2</h2>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Combats effectués :{" "}
              <span className="font-semibold">{uniqueCombatsDone}</span> /{" "}
              {MAX_COMBATS}
            </div>
          </div>
        </div>
      </div>

      {/* Progression */}
      <div className="mb-4 rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white dark:bg-slate-900 p-3">
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-300">
            Progression des combats
          </div>
          <div className="text-sm">
            Restants : <span className="font-semibold">{remaining}</span>
          </div>
        </div>
        <div className="mt-2 h-2 w-full rounded-full bg-gray-200 dark:bg-slate-800 overflow-hidden">
          <div
            className={`h-2 ${
              percent >= 66
                ? "bg-emerald-500"
                : percent >= 33
                  ? "bg-amber-500"
                  : "bg-red-500"
            }`}
            style={{ width: `${percent}%` }}
          />
        </div>
        <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          {percent}%
        </div>
      </div>

      {/* Tableau imprimable + boutons combats */}
      <div className="w-full overflow-auto rounded-xl border border-gray-200/70 dark:border-slate-700/70 bg-white dark:bg-slate-900">
        <div
          ref={printRef}
          className="p-4"
          style={{
            backgroundColor: "#fff",
            color: "#000",
            fontSize: "12px",
            fontFamily: "Arial, sans-serif",
            minWidth: 880,
          }}
        >
          <h3 className="text-base font-bold mb-3">
            Distribution N° {distributionId}
          </h3>

          <table className="table-auto border-collapse w-full mb-3 font-roboto">
            <thead>
              <tr className="bg-gray-100 text-gray-800 text-sm">
                <th className="border px-2 py-2">N°</th>
                <th className="border px-2 py-2">Compétiteurs</th>
                <th className="border px-2 py-2">Clubs</th>
                <th className="border px-2 py-2">Poids</th>
                <th className="border px-2 py-2">Ceinture</th>
                <th className="border px-2 py-2">V</th>
                <th className="border px-2 py-2">Pts</th>
                <th className="border px-2 py-2">Class.</th>
              </tr>
            </thead>
            <tbody>
              {orderedCompetiteurs.map((c, i) => {
                const rowClassement = classement.find(
                  (x) => Number(x.id) === Number(c.id),
                );
                const victories = rowClassement?.victoires ?? 0;
                const points = rowClassement?.points ?? 0;
                const isDQ = !!rowClassement?.disqualifie;

                let classementCell = "";
                if (classementVisible) {
                  if (isDQ) classementCell = "Disq.";
                  else
                    classementCell = rowClassement?.exAequo
                      ? `${rowClassement?.position ?? ""} (ex aequo)`
                      : (rowClassement?.position ?? "");
                }

                return (
                  <tr key={c.id} className="odd:bg-white even:bg-gray-50">
                    <td className="border px-2 py-2 text-center text-sm font-semibold w-8">
                      {i + 1}
                    </td>
                    <td className="border px-2 py-2 text-left font-semibold">
                      {String(c.nom_competiteur || "").toLocaleUpperCase(
                        "fr-FR",
                      )}{" "}
                      <span className="font-normal">
                        {capitalizeFr(c.prenom_competiteur)}
                      </span>
                    </td>
                    <td className="border px-2 py-2 text-left text-xs">
                      {c.club_competiteur}
                    </td>
                    <td className="border px-2 py-2 text-center text-xs">
                      {c.poids_competiteur ?? "—"}
                    </td>
                    <td className="border px-2 py-2 text-center text-xs">
                      {c.grade_competiteur || "—"}
                    </td>
                    <td className="border px-2 py-2 text-center text-sm font-semibold">
                      {rowClassement?.victoires ?? 0}
                    </td>
                    <td className="border px-2 py-2 text-center text-sm font-semibold">
                      {rowClassement?.points ?? 0}
                    </td>
                    <td
                      className={`border px-2 py-2 text-center text-sm font-semibold ${
                        isDQ ? "bg-gray-300" : ""
                      }`}
                    >
                      {classementCell}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {/* Boutons combats (deviennent verts quand un combat correspondant existe) */}
          <div className="mt-2 mb-2 flex flex-wrap items-center gap-2">
            <button
              onClick={() => openModal(1)}
              className={`px-4 py-2 rounded text-white ${
                getCombatByIndex(1)
                  ? "bg-emerald-600"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              title={tooltipFor(1, "Combat Aller")}
            >
              Combat Aller
            </button>

            <button
              onClick={() => openModal(2)}
              className={`px-4 py-2 rounded text-white ${
                getCombatByIndex(2)
                  ? "bg-emerald-600"
                  : "bg-blue-600 hover:bg-blue-700"
              }`}
              title={tooltipFor(2, "Combat Retour")}
            >
              Combat Retour
            </button>

            {(needThirdCombat() || getCombatByIndex(3)) && (
              <button
                onClick={() => openModal(3)}
                className={`px-4 py-2 rounded text-white ${
                  getCombatByIndex(3)
                    ? "bg-emerald-700"
                    : "bg-rose-600 hover:bg-rose-700"
                }`}
                title={tooltipFor(3, "Combat Décisif")}
              >
                Combat Décisif
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Action bar collante */}
      <div className="sticky bottom-2 mt-3 flex flex-wrap gap-2 justify-end">
        <div className="inline-flex gap-2 rounded-xl bg-white/90 dark:bg-slate-900/90 backdrop-blur border border-gray-200 dark:border-slate-700 p-2 shadow">
          <button
            onClick={() => setIsDetailsOpen(true)}
            className="rounded-lg bg-slate-700 text-white px-3 py-2 hover:bg-slate-800"
            title="Voir la liste des combats et leurs détails"
          >
            Détails des combats
          </button>
          <button
            onClick={refreshPouleTable}
            className="rounded-lg bg-blue-600 text-white px-3 py-2 hover:bg-blue-700"
          >
            Recharger
          </button>
          <button
            onClick={() => setClassementVisible((v) => !v)}
            className="rounded-lg bg-emerald-600 text-white px-3 py-2 hover:bg-emerald-700"
          >
            {classementVisible ? "Masquer Classement" : "Afficher Classement"}
          </button>
          <button
            onClick={handleExportToPDF}
            className="rounded-lg bg-slate-800 text-white px-3 py-2 hover:bg-slate-900"
            title="Exporter en PDF"
          >
            📄 Exporter PDF
          </button>
          <button
            onClick={confirmAndFinalize}
            className="rounded-lg bg-rose-600 text-white px-3 py-2 hover:bg-rose-700"
          >
            Finaliser
          </button>
        </div>
      </div>

      {/* Modales */}
      <CombatModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        competiteur1={selectedCombat?.competiteur1}
        competiteur2={selectedCombat?.competiteur2}
        onSubmit={handleModalSubmit}
        initialData={selectedCombat || {}}
        distributionId={distributionId}
        position1={positions.position1}
        position2={positions.position2}
        refreshPouleTable={refreshPouleTable}
        competitionId={competitionId}
        tapisId={tapisId}
        discipline={discipline} // ✅ ajout
      />
      <EditCombatModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        competiteur1={selectedCombat?.competiteur1}
        competiteur2={selectedCombat?.competiteur2}
        initialData={selectedCombat}
        distributionId={distributionId}
        position1={positions.position1}
        position2={positions.position2}
        refreshPouleTable={refreshPouleTable}
        discipline={discipline} // ✅ ajout
      />
      <CombatDetailsModal
        isOpen={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        combats={combats}
        onChanged={() => fetchCombats()}
      />
    </div>
  );
};

export default RepartitionPouleDeux;
